import React, { useState, useMemo } from 'react';
import RoleBasedSidebar from '../../components/ui/RoleBasedSidebar';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import QuoteFilters from './components/QuoteFilters';
import QuoteStats from './components/QuoteStats';
import QuoteList from './components/QuoteList';
import QuoteModal from './components/QuoteModal';

const QuotesManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [userRole] = useState('admin');
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: ''
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'view',
    selectedQuote: null
  });

  const mockQuotes = [
    {
      id: 3,
      folio: 'ORD-2026-003',
      customer: 'Carlos López',
      phone: '612-456-7890',
      vehicle: 'Nissan Versa 2021',
      plates: 'DEF-456',
      status: 'cotizada',
      technician: 'Fernando Ruiz',
      date: '2026-02-15',
      expirationDate: '2026-02-22',
      itemCount: 4,
      total: 380.00,
      discount: 0,
      items: [
        { id: 1, type: 'product', name: 'Llanta 195/55 R16', quantity: 4, price: 95.00 }
      ],
      notes: 'Cotización para cambio completo de llantas'
    },
    {
      id: 7,
      folio: 'ORD-2026-007',
      customer: 'Patricia Gómez',
      phone: '612-789-0123',
      vehicle: 'Volkswagen Jetta 2020',
      plates: 'PQR-901',
      status: 'cotizada',
      technician: 'Miguel Sánchez',
      date: '2026-02-14',
      expirationDate: '2026-02-21',
      itemCount: 3,
      total: 215.00,
      discount: 0,
      items: [
        { id: 1, type: 'service', name: 'Cambio de aceite', quantity: 1, price: 45.00 },
        { id: 2, type: 'service', name: 'Alineación', quantity: 1, price: 60.00 },
        { id: 3, type: 'product', name: 'Filtro de aire', quantity: 2, price: 15.00 }
      ],
      notes: 'Cliente solicita cotización para mantenimiento preventivo'
    },
    {
      id: 8,
      folio: 'ORD-2026-008',
      customer: 'Ricardo Torres',
      phone: '612-890-1234',
      vehicle: 'Hyundai Accent 2019',
      plates: 'STU-234',
      status: 'cotizada',
      technician: 'Roberto Díaz',
      date: '2026-02-13',
      expirationDate: '2026-02-20',
      itemCount: 2,
      total: 170.00,
      discount: 10.00,
      items: [
        { id: 1, type: 'product', name: 'Llanta 185/65 R15', quantity: 2, price: 85.00 }
      ],
      notes: 'Cotización para reemplazo de llantas delanteras'
    },
    {
      id: 9,
      folio: 'ORD-2026-009',
      customer: 'Sofía Ramírez',
      phone: '612-901-2345',
      vehicle: 'Kia Rio 2021',
      plates: 'VWX-567',
      status: 'cotizada',
      technician: 'Fernando Ruiz',
      date: '2026-02-15',
      expirationDate: '2026-02-22',
      itemCount: 5,
      total: 425.00,
      discount: 0,
      items: [
        { id: 1, type: 'service', name: 'Balanceo', quantity: 1, price: 40.00 },
        { id: 2, type: 'service', name: 'Alineación', quantity: 1, price: 60.00 },
        { id: 3, type: 'product', name: 'Llanta 195/55 R16', quantity: 4, price: 95.00 }
      ],
      notes: 'Cotización completa para cambio de llantas con servicios incluidos'
    }
  ];

  const filteredQuotes = useMemo(() => {
    return mockQuotes?.filter(quote => {
      const matchesSearch = !filters?.search || 
        quote?.customer?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        quote?.phone?.includes(filters?.search) ||
        quote?.folio?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        quote?.vehicle?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        quote?.plates?.toLowerCase()?.includes(filters?.search?.toLowerCase());

      const matchesDateFrom = !filters?.dateFrom || quote?.date >= filters?.dateFrom;
      const matchesDateTo = !filters?.dateTo || quote?.date <= filters?.dateTo;

      return matchesSearch && matchesDateFrom && matchesDateTo;
    });
  }, [filters, mockQuotes]);

  const stats = useMemo(() => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const totalValue = mockQuotes?.reduce((sum, q) => sum + (q?.total - q?.discount), 0);
    const avgQuoteValue = mockQuotes?.length > 0 ? totalValue / mockQuotes?.length : 0;
    
    const expiredQuotes = mockQuotes?.filter(q => q?.expirationDate < today)?.length;
    const expiringToday = mockQuotes?.filter(q => q?.expirationDate === today)?.length;

    return {
      total: mockQuotes?.length,
      pending: mockQuotes?.length,
      totalValue: totalValue,
      avgValue: avgQuoteValue,
      expired: expiredQuotes,
      expiringToday: expiringToday
    };
  }, [mockQuotes]);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleCreateQuote = () => {
    setModalState({
      isOpen: true,
      mode: 'create',
      selectedQuote: null
    });
  };

  const handleViewQuote = (quote) => {
    setModalState({
      isOpen: true,
      mode: 'view',
      selectedQuote: quote
    });
  };

  const handleEditQuote = (quote) => {
    setModalState({
      isOpen: true,
      mode: 'edit',
      selectedQuote: quote
    });
  };

  const handleConvertToOrder = (quote) => {
    console.log('Convirtiendo cotización a orden:', quote);
    alert(`Cotización ${quote?.folio} convertida a orden con estado "Aprobada"`);
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      mode: 'view',
      selectedQuote: null
    });
  };

  const handleSaveQuote = (quoteData) => {
    console.log('Guardando cotización:', quoteData);
    handleCloseModal();
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        userRole={userRole}
      />
      <main
        className={`transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-6">
          <NavigationBreadcrumb />

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Gestión de Cotizaciones
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Administra cotizaciones pendientes y conviértelas en órdenes aprobadas
            </p>
          </div>

          <QuoteStats stats={stats} />

          <QuoteFilters
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={handleClearFilters}
            onCreateQuote={handleCreateQuote}
          />

          <QuoteList
            quotes={filteredQuotes}
            onView={handleViewQuote}
            onEdit={handleEditQuote}
            onConvert={handleConvertToOrder}
            loading={loading}
          />
        </div>
      </main>

      <QuoteModal
        quote={modalState?.selectedQuote}
        isOpen={modalState?.isOpen}
        onClose={handleCloseModal}
        onSave={handleSaveQuote}
        onConvert={handleConvertToOrder}
        mode={modalState?.mode}
        userRole={userRole}
      />
    </div>
  );
};

export default QuotesManagement;